import { atom, SetStateAction } from 'jotai';
import { createPaginationAtoms } from './utils/filtersFactory';
import { atomWithStorage, createJSONStorage, RESET } from 'jotai/utils';
import { isEqual } from 'lodash';
import { focusAtom } from 'jotai-optics';
import {
  TariffSumaryInnerColumnSorting,
  TariffSummaryColumnName,
} from 'products/tariffSummary/Inventory/models/tariffSummaryModels';
import {
  mapURLSearchParamsToTariffSummaryFilters,
  setTariffSummarySortingParams,
} from './utils/filterMappers';
import { mapURLSearchParamsToSorting } from 'src/common/components/Filters/constants/sorting';
import { ObjectValues } from 'src/common/tsHelpers';

export const AlertStatusType = {
  AlertOn: 'alertOn',
  AlertAny: 'alertAny',
  AlertOff: 'alertOff',
} as const;
export type AlertStatusValue = ObjectValues<typeof AlertStatusType>;

export const {
  rowsPerPageFilterAtom: tariffSummaryRowsPerPageFilterAtom,
  pageFilterAtom: tariffSummaryPageFilterAtom,
} = createPaginationAtoms();

export type TarrifSummaryAtomValues = { account: string | null; alertStatus: AlertStatusValue };

export const INITIAL_FILTER_VALUES = {
  account: null,
  alertStatus: AlertStatusType.AlertAny,
};

const initialTariffSummaryFiltersAtom = atom<TarrifSummaryAtomValues>(INITIAL_FILTER_VALUES);

export const tariffSummaryFiltersAtom = atom(
  (get) => get(initialTariffSummaryFiltersAtom),
  (get, set, newValue: SetStateAction<TarrifSummaryAtomValues> | typeof RESET) => {
    const currentFilters = get(initialTariffSummaryFiltersAtom);
    if (!isEqual(currentFilters, newValue)) {
      set(tariffSummaryPageFilterAtom, 0);
    }

    if (newValue === RESET) {
      return set(initialTariffSummaryFiltersAtom, INITIAL_FILTER_VALUES);
    }

    return set(initialTariffSummaryFiltersAtom, newValue);
  },
);

export const accountsTariffSummaryAtom = focusAtom(tariffSummaryFiltersAtom, (optic) =>
  optic.prop('account'),
);

export const alertStatusTariffSummaryAtom = focusAtom(tariffSummaryFiltersAtom, (optic) =>
  optic.prop('alertStatus'),
);

const atomLocalStorage = createJSONStorage<TariffSumaryInnerColumnSorting>(() => localStorage);

const STORAGE_VERSION = 'v1';
const STORAGE_NAME = `tariff-summary-order`;
const STORAGE_KEY = `${STORAGE_VERSION}-${STORAGE_NAME}`;

export const tariffSummarySortingAtomWithStorage = atomWithStorage<TariffSumaryInnerColumnSorting>(
  STORAGE_KEY,
  {
    columnName: 'bundleProductCode',
    sort: 'asc',
  } as TariffSumaryInnerColumnSorting,
  atomLocalStorage,
  { getOnInit: true },
);

export const tariffSummaryUrlParamsAtom = atom(
  (get) => {
    const { account, alertStatus } = get(tariffSummaryFiltersAtom);
    const searchParams = new URLSearchParams();

    if (account) {
      searchParams.set('account', account);
    }

    if (alertStatus) {
      searchParams.set('alertStatus', alertStatus);
    }

    const sorting = get(tariffSummarySortingAtomWithStorage);

    setTariffSummarySortingParams({ sorting, searchParams });

    return searchParams;
  },
  (_, set, urlSearchParams: URLSearchParams) => {
    const filters = mapURLSearchParamsToTariffSummaryFilters(urlSearchParams);
    set(tariffSummaryFiltersAtom, filters);

    const sort = mapURLSearchParamsToSorting<TariffSummaryColumnName>(urlSearchParams);
    if (sort) {
      set(tariffSummarySortingAtomWithStorage, sort);
    }
  },
);
